import { useState, useMemo, useCallback, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import MapProvider from '@alltrails/maps/components/MapProvider';
import { fetchCollaborators } from '@alltrails/shared/utils/requests/listRequests';
import { completedListId, favoritesListId } from '@alltrails/modules/Lists/listUtils';
import { PageStrings } from '@alltrails/shared/utils/constants/pageStringHelpers';
import ListCollaborator from '@alltrails/shared/types/ListCollaborator';
import { GeoLocationProvider } from 'components/explore/GeoLocationProvider';
import SearchInsightsProvider from 'components/SearchInsightsProvider';
import ErrorFallback from 'components/shared/ErrorFallback';
import { Page, PageSource } from 'components/shared/Page/Page';
import useResizeListener from 'hooks/useResizeListener';
import { Context } from '@alltrails/shared/types/context';
import logError from 'utils/logError';
import CustomProvider from 'components/CustomProvider';
import SignupModal from 'components/SignupModal';
import useToggle from '@alltrails/shared/hooks/useToggle';
import useExperiment from 'hooks/useExperiment';
import { pathToRegexp } from 'path-to-regexp';
import useUser from 'hooks/useUser';
import { MapExpandProvider } from 'components/MapExpandControl/MapExpandProvider';
import { GarminProvider } from 'components/GarminSettings/GarminProvider/GarminProvider';
import compareUserIds from '@alltrails/shared/utils/compareUserIds';
import useListItems from '../../hooks/useListItems';
import NewFullscreenSearchApp from './NewFullscreenSearchApp';

type Props = {
  context: Context;
  initialCustomList: any;
  initialMyLists: any;
  initialMyListItems: any;
  initialPage: string;
  initialUserListItems: any;
  userInfo: any;
  locationData: {
    city: {
      names: { [key: string]: any[] };
    };
    country: string;
    latLng: (string | number)[];
  };
  trailPageProps?: any;
  initialSelectedObject?: any;
  initialExploreMap?: any;
};

const listAccessForAdminOrCurrentUserOrCollaborator = (
  { context, userInfo }: Props,
  page: string,
  collaborativeListsFeatureFlag: boolean,
  collaborators?: ListCollaborator[]
) => {
  if (context.adminUser && page === PageStrings.EXPLORE_COMPLETED_PAGE) {
    return true;
  }

  if (collaborativeListsFeatureFlag && page === PageStrings.EXPLORE_CUSTOM_PAGE && collaborators) {
    return collaborators.some(collaborator => collaborator.user.id === context?.currentUser?.id);
  }

  return compareUserIds(userInfo?.id, context?.currentUser?.id);
};

const SearchApp = (props: Props) => {
  const user = useUser();
  const [isOpen, toggle] = useToggle(!user && pathToRegexp('/:locale?/explore/map/new').test(__AT_DATA__.pathname));
  const [page, setPage] = useState(props.initialPage || PageStrings.EXPLORE_ALL_PAGE);
  const [isMobileWidth, setIsMobileWidth] = useState(props.context.mobileBrowser || false);
  const experiment = useExperiment('web-community-collaborative-lists');
  const collaborativeListsFeatureFlag = experiment?.value === 'on';
  const listId = useMemo(() => {
    switch (page) {
      case PageStrings.EXPLORE_COMPLETED_PAGE:
        return completedListId;
      case PageStrings.EXPLORE_CUSTOM_PAGE:
        return props.initialCustomList?.id.toString();
      case PageStrings.EXPLORE_FAVORITE_PAGE:
        return favoritesListId;
      default:
        return null;
    }
  }, [page, props.initialCustomList]);
  const exploreTrailId = props.trailPageProps?.trail?.verified_map_id;
  const activityTrailId = props?.initialSelectedObject?.ID;
  const verifiedMapId = exploreTrailId || activityTrailId;

  const [collaborators, setCollaborators] = useState<ListCollaborator[]>([]);
  const [areCollaboratorsLoading, setAreCollaboratorsLoading] = useState<boolean>(false);
  const belongsToCurrentUser = listAccessForAdminOrCurrentUserOrCollaborator(props, page, collaborativeListsFeatureFlag, collaborators);

  const { listMethods, renderModal, renderCollabListPrivacyModal, renderListToast, listItems, lists, latestListAction, userListItems } = useListItems(
    {
      lists: props.initialMyLists,
      listItems: props.initialMyListItems,
      userListItems: props.initialUserListItems,
      belongsToCurrentUser: belongsToCurrentUser && listId // this decides whether or not to edit an individual users list
    },
    page
  );

  useEffect(() => {
    if (collaborativeListsFeatureFlag && page === PageStrings.EXPLORE_CUSTOM_PAGE) {
      setAreCollaboratorsLoading(true);
      const getCollaborators = async () => {
        try {
          const data = await fetchCollaborators(listId);
          setCollaborators(data.collaborators);
        } catch (e) {
          logError('Fetching list collaborators error: ', e);
        } finally {
          setAreCollaboratorsLoading(false);
        }
      };

      getCollaborators();
    }
  }, [collaborativeListsFeatureFlag, listId, page]);

  const updatePage = (newPage: string) => {
    setPage(newPage);
  };

  const retryErrorPage = () => {
    window.location.reload();
  };

  const onResize = useCallback(() => {
    setIsMobileWidth(window.innerWidth <= 767);
  }, []);

  useResizeListener({ onResize });

  return (
    <Page pageSource={PageSource.Explore} context={props.context}>
      <SignupModal
        trigger={CarouselDisplayTrigger.CreateMap}
        isOpen={isOpen}
        onRequestClose={() => {
          window.location.assign(document.referrer);
        }}
        onSuccess={() => {
          toggle();
        }}
      />
      <GeoLocationProvider>
        <SearchInsightsProvider context={props.context} pageName={PageSource.Explore}>
          {renderModal()}
          {renderCollabListPrivacyModal()}
          {renderListToast()}
          <ErrorBoundary fallback={<ErrorFallback retryErrorPage={retryErrorPage} />} onError={e => logError(e)}>
            <MapProvider>
              <MapExpandProvider>
                <GarminProvider user={user} mapId={verifiedMapId} garminCourse={props.initialExploreMap?.garminCourse}>
                  <NewFullscreenSearchApp
                    {...props}
                    isMobileWidth={isMobileWidth}
                    latestListAction={latestListAction}
                    lists={lists}
                    listItems={listItems}
                    page={page}
                    userListItems={userListItems}
                    listMethods={listMethods}
                    listId={listId}
                    belongsToCurrentUser={belongsToCurrentUser}
                    updatePage={updatePage}
                    collaborators={collaborators}
                    setCollaborators={setCollaborators}
                    areCollaboratorsLoading={areCollaboratorsLoading}
                  />
                </GarminProvider>
              </MapExpandProvider>
            </MapProvider>
          </ErrorBoundary>
        </SearchInsightsProvider>
      </GeoLocationProvider>
    </Page>
  );
};

export default function SearchAppWrapper(props: Props) {
  return (
    <CustomProvider>
      <SearchApp {...props} />
    </CustomProvider>
  );
}
